
import AppToolbar from '@/_components/bar/appToolbar/appToolbar.vue'
import ProjectMixin from '@/_mixins/project'
import { getRequest, patchRequest, postRequest, deleteRequest } from '@/plugins/api-axios'
import { getURL } from '@/utils/axios'
import { deserialise, serialise } from 'kitsu-core'
import HelpText from '@/views/Project/Settings/Components/HelpText.vue'
import SaveStatusIndicator from '@/components/saveStatusIndicator.vue'
import { debounce } from 'lodash'
import type { ContractorRole } from '@/views/Project/types/contractor'

type ProjectSettings = {
    helpText: Maybe<Record<string, string>>
    type: string
    id: string
}

class ProjectSettingsViewModel {
    helpText: Maybe<Record<string, string>> = null
    type = ''
    id = ''

    saveStatus = null as Maybe<'saved' | 'saving'>

    constructor(data: ProjectSettings | null) {
        if (data) {
            this.helpText = data.helpText
            this.type = data.type
            this.id = data.id
        }
    }

    get payload() {
        return serialise(this.type, {
            id: this.id,
            helpText: this.helpText,
        }).data
    }
}

class ContractorRoleItemViewModel {
    id = ''
    name = ''

    isUpdating = false
    isDeleting = false

    constructor(data: ContractorRole) {
        this.id = data.id
        this.name = data.name
    }

    clone() {
        return Object.assign({}, this)
    }
}

class ContractorRoleViewModel {
    roles: ContractorRoleItemViewModel[] = []
    newRoleName = ''
    isAddingRole = false

    roleEditMenuActivatorEl = ''
    isRoleEditMenuOpen = false
    selectedRole = null as Maybe<ContractorRoleItemViewModel>

    constructor(data: ContractorRole[]) {
        if (data) {
            this.roles = data.map((role) => new ContractorRoleItemViewModel(role))
        }
    }

    get existingRolePayload() {
        if (!this.selectedRole) {
            return null
        }

        return serialise('contractorRole', {
            id: this.selectedRole.id,
            name: this.selectedRole.name,
        }).data
    }

    newRolePayload(hierarchyId: string) {
        return serialise('contractorRole', {
            name: this.newRoleName,
            hierarchy: {
                data: {
                    type: 'contractorHierarchy',
                    id: hierarchyId,
                },
            },
        }).data
    }

    addRole(role: ContractorRole) {
        this.roles.push(new ContractorRoleItemViewModel(role))
    }
}

export default {
    name: 'ContractorGeneralSetting',
    mixins: [ProjectMixin],
    components: {
        AppToolbar,
        HelpText,
        SaveStatusIndicator,
    },
    data() {
        return {
            settings: null as Maybe<ProjectSettingsViewModel>,
            contractorRole: new ContractorRoleViewModel([]),
            isLoading: true,
        }
    },
    computed: {
        hasContractorAdminPermission() {
            return this.hasPermission(this.allPermissions, 'contractor.admin')
        },
    },
    methods: {
        onEditRoleMenuVisibilityChange(visible: boolean) {
            if (visible) {
                return
            }

            this.contractorRole.selectedRole = null
            this.contractorRole.roleEditMenuActivatorEl = ''
            this.contractorRole.isRoleEditMenuOpen = false
        },
        async getSettings() {
            try {
                const projectSettingsResponse = await getRequest(
                    getURL({
                        path: `/projectSettings/${this.currentProjectId}`,
                        base: this.contractorBaseUrl,
                    })
                )
                this.settings = new ProjectSettingsViewModel(
                    deserialise(projectSettingsResponse.data).data
                )
            } finally {
                this.isLoading = false
            }
        },
        saveSettings: debounce(async function () {
            if (!this.settings) {
                return
            }

            try {
                this.settings.saveStatus = 'saving'
                const { data } = await patchRequest(
                    getURL({
                        path: `/projectSettings/${this.currentProjectId}`,
                        base: this.contractorBaseUrl,
                    }),
                    {},
                    this.settings.payload
                )
                this.settings = new ProjectSettingsViewModel(deserialise(data).data)
                this.settings.saveStatus = 'saved'
                setTimeout(() => {
                    if (this.settings) {
                        this.settings.saveStatus = null
                    }
                }, 2000)
            } catch {
                this.settings.saveStatus = null
            }
        }, 500),
        async getContractorRoles() {
            const projectHierarchyResponse = await getRequest(
                getURL({
                    path: `/contractorHierarchies/${this.currentProjectId}`,
                    base: this.contractorBaseUrl,
                    params: {
                        include: 'roles',
                    },
                })
            )
            this.contractorRole = new ContractorRoleViewModel(
                deserialise(projectHierarchyResponse.data).data.roles.data
            )
        },
        async editRole(role: ContractorRoleItemViewModel) {
            this.contractorRole.roleEditMenuActivatorEl = `#role-${role.id}`
            this.contractorRole.selectedRole = role.clone()
            this.contractorRole.isRoleEditMenuOpen = true
        },
        async deleteRole(role: ContractorRoleItemViewModel) {
            if (role.isDeleting) {
                return
            }

            try {
                role.isDeleting = true
                await deleteRequest(
                    getURL({
                        path: `contractorRoles/${role.id}`,
                        base: this.contractorBaseUrl,
                    })
                )
                this.contractorRole.roles = this.contractorRole.roles.filter(
                    (roleItem) => roleItem.id !== role.id
                )
            } finally {
                role.isDeleting = false
            }
        },
        async saveRole() {
            if (!this.contractorRole.selectedRole || this.contractorRole.selectedRole.isUpdating) {
                return
            }

            try {
                this.contractorRole.selectedRole.isUpdating = true
                const roleResponse = await patchRequest(
                    getURL({
                        path: `contractorRoles/${this.contractorRole.selectedRole.id}`,
                        base: this.contractorBaseUrl,
                    }),
                    {},
                    this.contractorRole.existingRolePayload
                )
                this.contractorRole.roles = this.contractorRole.roles.map((role) => {
                    if (role.id === this.contractorRole.selectedRole?.id) {
                        return new ContractorRoleItemViewModel(deserialise(roleResponse.data).data)
                    }
                    return role
                })
                this.onEditRoleMenuVisibilityChange(false)
            } finally {
                if (this.contractorRole.selectedRole) {
                    this.contractorRole.selectedRole.isUpdating = false
                }
            }
        },
        async addRole() {
            if (!this.contractorRole.newRoleName || this.contractorRole.isAddingRole) {
                return
            }

            if (
                this.contractorRole.roles.find(
                    (role) => role.name === this.contractorRole.newRoleName
                )
            ) {
                this.contractorRole.newRoleName = ''
                return
            }

            try {
                this.contractorRole.isAddingRole = true
                const roleResponse = await postRequest(
                    getURL({
                        path: 'contractorRoles',
                        base: this.contractorBaseUrl,
                    }),
                    {},
                    this.contractorRole.newRolePayload(this.currentProjectId)
                )
                this.contractorRole.addRole(deserialise(roleResponse.data).data)
                this.contractorRole.newRoleName = ''
            } finally {
                this.contractorRole.isAddingRole = false
            }
        },
    },
    created() {
        this.getSettings()
        this.getContractorRoles()
    },
}
